import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';

import { useGetQueueItemQuery } from '@/store/api/services/marketing/queue';
import { useGetUserQuery } from '@/store/api/services/users/users';
import type { LocationPublishedData, QueueItem } from '@/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTION_LABEL: Record<QueueItem['action'], string> = {
  create: 'Create',
  update: 'Update',
  unpublish: 'Unpublish',
  republish: 'Republish',
};

const STATUS_CHIP_COLOR: Record<QueueItem['status'], 'default' | 'error' | 'warning' | 'success'> =
  {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    error: 'error',
    obsolete: 'default',
    completed: 'success',
    conflict: 'warning',
  };

const STATUS_LABEL: Record<QueueItem['status'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  error: 'Error',
  obsolete: 'Obsolete',
  completed: 'Completed',
  conflict: 'Conflict',
};

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  location_description: 'Description',
  address: 'Address',
  address2: 'Address 2',
  address_street: 'Street',
  address_city: 'City',
  address_state: 'State',
  address_zip: 'ZIP Code',
  address_country: 'Country',
  city: 'City',
  state: 'State',
  country: 'Country',
  zipcode: 'ZIP Code',
  phone: 'Phone',
  contact_phone: 'Phone',
  latitude: 'Latitude',
  longitude: 'Longitude',
  place_id: 'Place ID',
  is_always_open: 'Always Open',
};

const humanize = (key: string): string =>
  FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Fields to skip (rendered separately, internal-only, or redundant)
const SKIP_FIELDS = new Set([
  'open_hours',
  'hours',
  'images',
  'image_urls',
  'is_always_open',
  'latitude',
  'longitude',
  'place_id',
  // address fields are combined into a single "Address" row
  'address',
  'address2',
  'address_street',
  'address_city',
  'address_state',
  'address_zip',
  'address_country',
  'city',
  'state',
  'country',
  'zipcode',
]);

const FIELD_ORDER: Record<string, number> = {
  name: 0,
  phone: 1,
  contact_phone: 1,
  address: 2,
  open_hours: 99,
  hours: 99,
};

const formatAddress = (data: LocationPublishedData): string => {
  const street = String(data.address_street ?? data.address ?? '');
  const city = String(data.address_city ?? data.city ?? '');
  const state = String(data.address_state ?? data.state ?? '');
  const zip = String(data.address_zip ?? data.zipcode ?? '');
  const country = String(data.address_country ?? data.country ?? '');
  const lines: string[] = [];
  if (street) lines.push(street);
  const cityLine = [city, state, zip].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);
  if (country) lines.push(country);
  return lines.join('\n');
};

// ---------------------------------------------------------------------------
// Open hours formatter
// ---------------------------------------------------------------------------

type OpenHourSpan = { open: string; close: string };
type DaySchedule = { state?: string; span?: OpenHourSpan[] };
type OpenHours = Record<string, DaySchedule>;

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const fmtTime = (t: string): string => {
  const h = Number.parseInt(t.slice(0, 2), 10);
  const m = t.slice(2);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(h12).padStart(2, '0')}:${m} ${period}`;
};

const hoursAreEqual = (a: OpenHours, b: OpenHours): boolean => {
  for (const day of DAY_ORDER) {
    const da = a[day];
    const db = b[day];
    if (!da && !db) continue;
    if (!da || !db) return false;
    if (da.state !== db.state) return false;
    const sa = da.span ?? [];
    const sb = db.span ?? [];
    if (sa.length !== sb.length) return false;
    for (let i = 0; i < sa.length; i++) {
      if (sa[i].open !== sb[i].open || sa[i].close !== sb[i].close) return false;
    }
  }
  return true;
};

const OpenHoursDisplay = ({ hours }: { hours: OpenHours }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {DAY_ORDER.map((day) => {
      const sched = hours[day];
      if (!sched) return null;
      const spans = sched.span ?? [];
      const lines: string[] =
        sched.state === 'AlwaysOpen'
          ? ['Always Open']
          : sched.state === 'Closed' || spans.length === 0
            ? ['Closed']
            : spans.map((s) => `${fmtTime(s.open)} – ${fmtTime(s.close)}`);
      return (
        <Box key={day} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ width: 30, flexShrink: 0, fontVariantNumeric: 'tabular-nums', pt: '1px' }}
          >
            {DAY_LABEL[day]}
          </Typography>
          <Box>
            {lines.map((line) => (
              <Typography key={line} variant="body2">
                {line}
              </Typography>
            ))}
          </Box>
        </Box>
      );
    })}
  </Box>
);

// ---------------------------------------------------------------------------
// Meta item (key/value pair used in the metadata strip)
// ---------------------------------------------------------------------------

const MetaItem = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography
      sx={{
        display: 'block',
        mb: 0.25,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'text.disabled',
      }}
    >
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Field row
// ---------------------------------------------------------------------------

const FieldRow = ({
  label,
  before,
  after,
}: {
  label: string;
  before?: React.ReactNode;
  after: React.ReactNode;
}) => {
  const changed = before !== undefined;
  const isSimple = typeof after === 'string';

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.5,
          color: 'text.disabled',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: 10,
        }}
      >
        {label}
      </Typography>

      {!changed ? (
        isSimple ? (
          <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
            {after || '—'}
          </Typography>
        ) : (
          <>{after}</>
        )
      ) : isSimple ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              textDecoration: before ? 'line-through' : 'none',
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
            }}
          >
            {(before as string) || '—'}
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ userSelect: 'none', mt: '1px' }}>
            →
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
          >
            {after || '—'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: 'block',
                mb: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: 10,
              }}
            >
              Before
            </Typography>
            {before}
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: 'block',
                mb: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: 10,
              }}
            >
              After
            </Typography>
            {after}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Build field rows from proposed_changes vs current_state
// ---------------------------------------------------------------------------

interface RenderedField {
  key: string;
  label: string;
  before: React.ReactNode | undefined;
  after: React.ReactNode;
}

const isPrimitive = (val: unknown): val is string | number | boolean =>
  val !== null && val !== undefined && typeof val !== 'object';

const buildFields = (
  proposed: LocationPublishedData,
  current: LocationPublishedData,
  isCreate: boolean,
): RenderedField[] => {
  const fields: RenderedField[] = [];

  // proposed_changes = desired new state, current_state = existing state
  // For updates: current_state is "before", proposed_changes is "after"
  // For creates: proposed_changes is the new listing data, no before
  const allKeys = new Set([...Object.keys(proposed), ...Object.keys(current ?? {})]);

  for (const key of allKeys) {
    if (SKIP_FIELDS.has(key)) continue;
    const p = proposed[key];
    const c = current?.[key];
    const pStr = isPrimitive(p) ? String(p) : null;
    const cStr = isPrimitive(c) ? String(c) : null;

    if (!isCreate && pStr === cStr) continue;
    if (isCreate && pStr === null) continue;
    if (!isCreate && pStr === null && cStr === null) continue;

    fields.push({
      key,
      label: humanize(key),
      before: !isCreate ? (cStr ?? '') : undefined,
      after: isCreate ? (pStr ?? '') : (pStr ?? ''),
    });
  }

  const proposedHours = proposed.open_hours ?? proposed.hours;
  const currentHours = current?.open_hours ?? current?.hours;

  if (isCreate) {
    if (proposedHours && typeof proposedHours === 'object' && !Array.isArray(proposedHours)) {
      fields.push({
        key: 'open_hours',
        label: 'Hours',
        before: undefined,
        after: <OpenHoursDisplay hours={proposedHours as OpenHours} />,
      });
    }
  } else {
    const hasCurrentHours =
      currentHours && typeof currentHours === 'object' && !Array.isArray(currentHours);
    const hasProposedHours =
      proposedHours && typeof proposedHours === 'object' && !Array.isArray(proposedHours);
    const hoursChanged =
      Boolean(hasCurrentHours) !== Boolean(hasProposedHours) ||
      (hasCurrentHours &&
        hasProposedHours &&
        !hoursAreEqual(currentHours as OpenHours, proposedHours as OpenHours));
    if (hoursChanged) {
      fields.push({
        key: 'open_hours',
        label: 'Hours',
        before: hasCurrentHours ? <OpenHoursDisplay hours={currentHours as OpenHours} /> : '',
        after: hasProposedHours ? <OpenHoursDisplay hours={proposedHours as OpenHours} /> : '',
      });
    }
  }

  // Address (combine individual fields into one row)
  const proposedAddr = formatAddress(proposed);
  const currentAddr = formatAddress(current ?? {});
  if (isCreate && proposedAddr) {
    fields.push({ key: 'address', label: 'Address', before: undefined, after: proposedAddr });
  } else if (!isCreate && proposedAddr !== currentAddr) {
    fields.push({
      key: 'address',
      label: 'Address',
      before: currentAddr || '',
      after: proposedAddr || '',
    });
  }

  fields.sort((a, b) => (FIELD_ORDER[a.key] ?? 50) - (FIELD_ORDER[b.key] ?? 50));

  return fields;
};

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const QueueItemDetail = ({ hide, props }: ModalChildrenProps) => {
  const listItem = props?.data?.item as QueueItem | undefined;
  const {
    data: { data: item } = {},
    isFetching,
  } = useGetQueueItemQuery(listItem?.id ?? '', {
    skip: !listItem?.id,
  });

  const reviewedById = item?.reviewed_by ?? listItem?.reviewed_by ?? null;
  const { data: reviewer } = useGetUserQuery(reviewedById!, {
    skip: reviewedById == null,
  });

  if (!listItem) return null;

  const isCreate = listItem.action === 'create';
  const fields = item?.proposed_changes
    ? buildFields(item.proposed_changes, item.current_state ?? {}, isCreate)
    : [];

  const changedAt = listItem.change_occurred_at
    ? format(parseISO(listItem.change_occurred_at), 'MMM d, yyyy h:mm a')
    : null;

  const reviewedAt = item?.reviewed_at
    ? format(parseISO(item.reviewed_at), 'MMM d, yyyy h:mm a')
    : null;

  const processedAt = item?.processed_at
    ? format(parseISO(item.processed_at), 'MMM d, yyyy h:mm a')
    : null;

  const status = item?.status ?? listItem.status;

  return (
    <>
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 1.25, lineHeight: 1.25 }}>
            {listItem.location_name ?? `Location ${listItem.location_id}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={ACTION_LABEL[listItem.action]}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}
            />
            {status && (
              <Chip
                label={STATUS_LABEL[status]}
                size="small"
                color={STATUS_CHIP_COLOR[status]}
                sx={{ height: 22, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.disabled">
            Location {listItem.location_id}
            {changedAt ? ` · ${changedAt}` : ''}
          </Typography>
        </Box>

        {/* Metadata strip */}
        {(listItem.operator_name || reviewedAt || processedAt) && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
            }}
          >
            {listItem.operator_name && <MetaItem label="Operator" value={listItem.operator_name} />}
            {reviewedById != null && reviewer && (
              <MetaItem
                label="Reviewed by"
                value={`${reviewer.first_name} ${reviewer.last_name}`}
              />
            )}
            {reviewedAt && <MetaItem label="Reviewed at" value={reviewedAt} />}
            {processedAt && <MetaItem label="Processed" value={processedAt} />}
            {status === 'error' && item?.retry_attempts != null && (
              <MetaItem
                label="Retries"
                value={
                  item.is_retryable_error === false
                    ? `${item.retry_attempts} · not retryable`
                    : String(item.retry_attempts)
                }
              />
            )}
          </Box>
        )}

        {/* Rejection reason */}
        {item?.rejection_reason && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 3,
                borderRadius: 0.5,
                bgcolor: 'error.main',
                flexShrink: 0,
                alignSelf: 'stretch',
                minHeight: 16,
              }}
            />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.5,
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Rejection reason
              </Typography>
              <Typography variant="body2">{item.rejection_reason}</Typography>
            </Box>
          </Box>
        )}

        {/* Error details */}
        {item?.error_details &&
          (() => {
            const CONFLICT_PREFIX =
              'Found the following differences between Queue Item and PinMeTo current state:';
            let message: string = item.error_details;
            let errors: string[] = [];
            let severity: 'error' | 'warning' = 'error';

            if (item.error_details.startsWith(CONFLICT_PREFIX)) {
              const fieldNames = item.error_details
                .slice(CONFLICT_PREFIX.length)
                .split('\n')
                .map((line) => line.split(':')[0]?.trim())
                .filter((s): s is string => Boolean(s));
              message =
                "PinMeTo's listing changed after this update was queued. Review the conflicting fields below before approving.";
              errors = fieldNames;
              severity = 'warning';
            } else {
              try {
                const parsed = JSON.parse(item.error_details);
                if (parsed?.message) message = parsed.message;
                if (Array.isArray(parsed?.errors)) errors = parsed.errors;
              } catch {}
            }

            return (
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Alert severity={severity}>
                  <Typography variant="body2" fontWeight={600}>
                    {message}
                  </Typography>
                  {errors.length > 0 && (
                    <List dense disablePadding sx={{ mt: 0.5 }}>
                      {errors.map((err) => (
                        <ListItem key={err} disableGutters sx={{ py: 0 }}>
                          <ListItemText
                            primary={`• ${err}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Alert>
              </Box>
            );
          })()}

        {/* Field changes */}
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : fields.length === 0 ? (
          <Box sx={{ px: 3, py: 4 }}>
            <Typography variant="body2" color="text.disabled">
              No field data available.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 3, py: 3 }}>
            <Typography
              variant="overline"
              color="text.disabled"
              sx={{ display: 'block', mb: 2.5, lineHeight: 1 }}
            >
              {isCreate
                ? 'Listing data'
                : `${fields.length} field${fields.length !== 1 ? 's' : ''} changed`}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {fields.map((f) => (
                <FieldRow key={f.key} label={f.label} before={f.before} after={f.after} />
              ))}
            </Box>
          </Box>
        )}

        {/* Images: extracted from proposed_changes.image_urls or .images */}
        {(() => {
          const raw = item?.proposed_changes?.image_urls ?? item?.proposed_changes?.images;
          const imageUrls = Array.isArray(raw)
            ? (raw as unknown[])
                .map((img) => (typeof img === 'string' ? img : (img as { url?: string })?.url))
                .filter((u): u is string => typeof u === 'string' && u.length > 0)
            : [];
          if (imageUrls.length === 0) return null;
          return (
            <Box sx={{ px: 3, pt: 0, pb: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography
                variant="overline"
                color="text.disabled"
                sx={{ display: 'block', mt: 2.5, mb: 1.5, lineHeight: 1 }}
              >
                Images ({imageUrls.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {imageUrls.map((url) => (
                  <Box
                    key={url}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      width: 96,
                      height: 96,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      flexShrink: 0,
                      '&:hover img': { opacity: 0.85 },
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt=""
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'opacity 0.15s',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })()}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={hide} disableElevation>
          Close
        </Button>
      </DialogActions>
    </>
  );
};

export default QueueItemDetail;
